/**
 * Creation dialogs of the Lines feature (W2-07; DESIGN.md §5.5 "Create"):
 *  - New N lines: count, name pattern (`fx_{k}`: {k} counts from 0, {n} from 1), type, parent, plus what the type
 *    needs (picture, GIF, HUD element, text); a preview of the first names; one undoable step;
 *  - picture picker for Image and GIF lines (the project's pictures; "Add a picture..." imports one);
 *  - HUD element picker for UI mover lines.
 */

import { useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import { expandNamePattern, MAX_BATCH_LINES } from '../../core/commands/lines';
import type { LinePreset } from '../../core/commands/types';
import type { AssetId, AssetRef, ChartDoc, LineId, UiElement } from '../../core/model/types';
import { useT } from '../../i18n/useT';
import type { DocStore } from '../../state/types';
import { Button } from '../../ui/Button';
import { Dialog } from '../../ui/Dialog';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { Select } from '../../ui/Select';
import { sameItems, useDocValue, useEditorValue } from './hooks';
import { addLine, addManyLines, lineTitle } from './lineActions';
import { m } from './lines.messages';
import { imageAssets, TYPE_ICONS, UI_ELEMENT_ICONS, UI_ELEMENT_IDS, type LineTypeId } from './lineModel';
import s from './Lines.module.css';

/** Presets the "New N lines" dialog offers, with the type they create and whether they are Full-level only. */
const PRESETS: readonly { readonly preset: LinePreset; readonly type: LineTypeId; readonly full: boolean }[] = [
  { preset: 'standard', type: 'line', full: false },
  { preset: 'text', type: 'text', full: false },
  { preset: 'image', type: 'image', full: false },
  { preset: 'helper', type: 'helper', full: true },
  { preset: 'gif', type: 'gif', full: true },
  { preset: 'paint', type: 'paint', full: true },
  { preset: 'uiMover', type: 'uiMover', full: true },
];

const NO_PARENT = 'none';

const lineOrderOf = (d: ChartDoc): readonly LineId[] => [...d.lineOrder];
const assetRecords = (d: ChartDoc): AssetRef[] => [...d.assets.values()];

function sizeOf(t: ReturnType<typeof useT>, a: AssetRef): string {
  const w = a.meta?.width;
  const h = a.meta?.height;
  return w !== undefined && h !== undefined ? t(m.imageSize, { w, h }) : '';
}

export interface NewLinesDialogProps {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly preset: LinePreset;
  readonly onClose: () => void;
}

export function NewLinesDialog(props: NewLinesDialogProps) {
  const { services, store } = props;
  const t = useT();
  const level = useEditorValue(services, (st) => st.level);
  const order = useDocValue(store, lineOrderOf, ['lineOrder'], sameItems);
  const assets = useDocValue(store, assetRecords, ['assets'], sameItems);
  const [countText, setCountText] = useState('8');
  const [pattern, setPattern] = useState('fx_{k}');
  const [preset, setPreset] = useState<LinePreset>(props.preset);
  const [parent, setParent] = useState<string>(NO_PARENT);
  const [texture, setTexture] = useState<string>('');
  const [element, setElement] = useState<UiElement>('score');
  const [text, setText] = useState('');
  const presets = PRESETS.filter((p) => !p.full || level === 'full' || p.preset === preset);
  const pool = preset === 'gif' || preset === 'image' ? imageAssets(assets, preset === 'gif') : [];
  const chosen = pool.some((a) => a.id === texture) ? texture : (pool[0]?.id ?? '');
  const count = Number(countText);
  const countOk = Number.isInteger(count) && count >= 1 && count <= MAX_BATCH_LINES;
  const needsPicture = (preset === 'gif' || preset === 'image') && chosen === '';
  const names = countOk
    ? Array.from({ length: Math.min(3, count) }, (_, k) =>
        pattern.trim() === ''
          ? t(m.defaultLineName, { index: order.length + k })
          : expandNamePattern(pattern.trim(), k, count),
      )
    : [];

  const create = (): void => {
    if (!countOk || needsPicture) return;
    const ids = addManyLines(services, {
      count,
      namePattern: pattern,
      preset,
      ...(parent !== NO_PARENT ? { parent: Number(parent) as LineId } : {}),
      ...((preset === 'image' || preset === 'gif') && chosen !== '' ? { texture: chosen as AssetId } : {}),
      ...(preset === 'uiMover' ? { attachUI: element } : {}),
      ...(preset === 'text' && text.trim() !== '' ? { text } : {}),
    });
    if (ids !== null) props.onClose();
  };

  return (
    <Dialog
      open={true}
      size="md"
      title={t(m.newLinesTitle)}
      onClose={props.onClose}
      footer={
        <>
          <Button variant="secondary" onClick={props.onClose}>
            {t(m.cancel)}
          </Button>
          <Button
            variant="primary"
            icon="ListPlus"
            disabledReason={
              !countOk
                ? { key: m.newLinesCountRange, params: { max: MAX_BATCH_LINES } }
                : needsPicture
                  ? { key: m.noImages }
                  : null
            }
            onClick={create}
          >
            {t(m.newLinesCreate, { count: countOk ? count : 0 })}
          </Button>
        </>
      }
    >
      <div className={s.form}>
        <div className={s.fieldRow}>
          <label className={s.label} htmlFor="lines-new-count">
            {t(m.newLinesCount)}
          </label>
          <input
            id="lines-new-count"
            className={s.input}
            type="number"
            min={1}
            max={MAX_BATCH_LINES}
            step={1}
            value={countText}
            aria-invalid={countOk ? undefined : true}
            onChange={(e) => {
              setCountText(e.target.value);
            }}
          />
          {!countOk && (
            <span className={s.help} role="alert">
              {t(m.newLinesCountRange, { max: MAX_BATCH_LINES })}
            </span>
          )}
        </div>
        <div className={s.fieldRow}>
          <label className={s.label} htmlFor="lines-new-pattern">
            {t(m.newLinesPattern)}
          </label>
          <input
            id="lines-new-pattern"
            className={s.input}
            value={pattern}
            spellCheck={false}
            onChange={(e) => {
              setPattern(e.target.value);
            }}
          />
          <span className={s.help}>{t(m.newLinesPatternHelp)}</span>
          {names.length > 0 && (
            <span className={s.help}>
              {t(m.newLinesPreview, { names: names.join(', '), more: Math.max(0, count - names.length) })}
            </span>
          )}
        </div>
        <Select<LinePreset>
          label={t(m.newLinesType)}
          value={preset}
          options={presets.map((p) => ({
            value: p.preset,
            label: t(m.typeName, { type: p.type }),
            icon: TYPE_ICONS[p.type],
          }))}
          onChange={(v) => {
            setPreset(v);
          }}
        />
        {(preset === 'image' || preset === 'gif') &&
          (pool.length === 0 ? (
            <div className={s.warning}>
              <Icon name="ImageOff" size={16} />
              <span>{t(preset === 'gif' ? m.noGifs : m.noImages)}</span>
            </div>
          ) : (
            <Select<string>
              label={t(preset === 'gif' ? m.pickGif : m.pickImage)}
              value={chosen}
              options={pool.map((a) => ({ value: a.id, label: a.path }))}
              onChange={(v) => {
                setTexture(v);
              }}
            />
          ))}
        {preset === 'uiMover' && (
          <Select<UiElement>
            label={t(m.pickElement)}
            value={element}
            options={UI_ELEMENT_IDS.map((e) => ({
              value: e,
              label: t(m.uiElementName, { element: e }),
              icon: UI_ELEMENT_ICONS[e],
            }))}
            onChange={(v) => {
              setElement(v);
            }}
          />
        )}
        {preset === 'text' && (
          <div className={s.fieldRow}>
            <label className={s.label} htmlFor="lines-new-text">
              {t(m.initialText)}
            </label>
            <input
              id="lines-new-text"
              className={s.input}
              value={text}
              placeholder={t(m.initialTextPlaceholder)}
              onChange={(e) => {
                setText(e.target.value);
              }}
            />
          </div>
        )}
        <Select<string>
          label={t(m.newLinesParent)}
          value={parent}
          options={[
            { value: NO_PARENT, label: t(m.pickerNoParent) },
            ...order.map((id) => ({ value: String(id), label: lineTitle(store, id) })),
          ]}
          onChange={(v) => {
            setParent(v);
          }}
        />
      </div>
    </Dialog>
  );
}

export interface ImagePickerProps {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly gif: boolean;
  readonly onClose: () => void;
}

/** Picture for a new Image or GIF line. */
export function ImagePickerDialog(props: ImagePickerProps) {
  const { services, store, gif } = props;
  const t = useT();
  const assets = useDocValue(store, assetRecords, ['assets'], sameItems);
  const pool = imageAssets(assets, gif);
  const addPicture = (): void => {
    void services.registry.run('assets.add', { role: gif ? 'gif' : 'texture' }).then((ran) => {
      if (!ran) services.ui.toast({ key: m.addPictureUnavailable }, { kind: 'warning' });
    });
  };
  return (
    <Dialog
      open={true}
      size="md"
      title={t(gif ? m.newGifTitle : m.newImageTitle)}
      description={t(gif ? m.newGifHelp : m.newImageHelp)}
      onClose={props.onClose}
      footer={
        <>
          <Button variant="secondary" icon="ImagePlus" onClick={addPicture}>
            {t(m.addPicture)}
          </Button>
          <Button variant="secondary" onClick={props.onClose}>
            {t(m.cancel)}
          </Button>
        </>
      }
    >
      {pool.length === 0 ? (
        <EmptyState icon="ImageOff" title={{ key: gif ? m.noGifs : m.noImages }} body={{ key: m.addPictureHelp }} />
      ) : (
        <ul className={s.tileGrid} aria-label={t(gif ? m.pickGif : m.pickImage)}>
          {pool.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                className={s.tile}
                onClick={() => {
                  if (addLine(services, gif ? 'gif' : 'image', { texture: a.id }) !== null) props.onClose();
                }}
              >
                <Icon name={a.meta?.codec === 'gif' ? 'Film' : 'Image'} size={18} />
                <span className={s.tileText}>
                  <span className={s.tileTitle}>{a.path}</span>
                  <span className={s.tileDetail}>{sizeOf(t, a)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

export interface UiElementPickerProps {
  readonly services: AppServices;
  readonly onClose: () => void;
}

/** HUD element for a new UI mover line. */
export function UiElementDialog(props: UiElementPickerProps) {
  const t = useT();
  return (
    <Dialog
      open={true}
      size="md"
      title={t(m.newUiMoverTitle)}
      description={t(m.newUiMoverHelp)}
      onClose={props.onClose}
      footer={
        <Button variant="secondary" onClick={props.onClose}>
          {t(m.cancel)}
        </Button>
      }
    >
      <ul className={s.tileGrid} aria-label={t(m.pickElement)}>
        {UI_ELEMENT_IDS.map((e) => (
          <li key={e}>
            <button
              type="button"
              className={s.tile}
              onClick={() => {
                if (addLine(props.services, 'uiMover', { attachUI: e }) !== null) props.onClose();
              }}
            >
              <Icon name={UI_ELEMENT_ICONS[e]} size={18} />
              <span className={s.tileText}>
                <span className={s.tileTitle}>{t(m.uiElementName, { element: e })}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
